import { HEARTBEAT_MS, isServerEvent, wsUrl, type ClientEvent, type ServerEvent } from '@ai-studio/shared';

type Listener = (event: ServerEvent) => void;
type StatusListener = (status: SocketStatus) => void;

export type SocketStatus = 'idle' | 'connecting' | 'open' | 'closed';

const MAX_BACKOFF_MS = 30_000;

/**
 * Reconnecting event socket.
 *
 * Sends a heartbeat well inside the reverse proxy's idle window so the
 * connection is never reaped, and backs off exponentially on failure so a
 * server restart does not turn into a reconnect storm.
 */
export class EventSocket {
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private lastSeq = 0;
  private projectId: number | null = null;
  private shouldRun = false;
  private status: SocketStatus = 'idle';

  connect(projectId: number | null = null): void {
    this.projectId = projectId;
    this.shouldRun = true;
    this.open();
  }

  private open(): void {
    if (!this.shouldRun) return;
    this.cleanup();
    this.setStatus(this.reconnectAttempt === 0 ? 'connecting' : 'connecting');

    let socket: WebSocket;
    try {
      socket = new WebSocket(wsUrl());
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.reconnectAttempt = 0;
      this.setStatus('open');
      this.send({ type: 'subscribe', projectId: this.projectId, since: this.lastSeq });
      this.heartbeat = setInterval(() => {
        this.send({ type: 'ping', t: Date.now() });
      }, HEARTBEAT_MS);
    };

    socket.onmessage = (message) => {
      let event: ServerEvent | null = null;
      try {
        const parsed: unknown = JSON.parse(String(message.data));
        if (isServerEvent(parsed)) event = parsed;
      } catch {
        event = null;
      }
      if (!event) return;

      // Track the cursor so a reconnect can resume rather than refetch.
      if (event.seq > this.lastSeq && event.type !== 'hello' && event.type !== 'pong') {
        this.lastSeq = event.seq;
      }
      if (event.type === 'resumed' && event.missed > 0) {
        // Too much was missed to replay; a full resync is the caller's job.
        this.lastSeq = 0;
      }

      for (const listener of this.listeners) {
        try {
          listener(event);
        } catch {
          // One bad subscriber must not stop the others.
        }
      }
    };

    socket.onclose = () => {
      this.setStatus('closed');
      this.scheduleReconnect();
    };

    socket.onerror = () => {
      // onclose always follows; reconnect is handled there.
    };
  }

  private scheduleReconnect(): void {
    if (!this.shouldRun) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectAttempt += 1;
    const delay = Math.min(MAX_BACKOFF_MS, 500 * 2 ** (this.reconnectAttempt - 1));
    this.reconnectTimer = setTimeout(() => this.open(), delay);
  }

  private cleanup(): void {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      if (this.socket.readyState === WebSocket.OPEN) this.socket.close();
      this.socket = null;
    }
  }

  private send(event: ClientEvent): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(event));
    }
  }

  private setStatus(status: SocketStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const listener of this.statusListeners) listener(status);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => this.statusListeners.delete(listener);
  }

  /** Answer a server `editor.request` (dropped when the socket is down; the server times out). */
  respond(requestId: string, outcome: { ok: true; result: unknown } | { ok: false; error: string }): void {
    this.send(
      outcome.ok
        ? { type: 'editor.response', requestId, ok: true, result: outcome.result }
        : { type: 'editor.response', requestId, ok: false, error: outcome.error },
    );
  }

  setProject(projectId: number | null): void {
    this.projectId = projectId;
    this.send({ type: 'subscribe', projectId, since: this.lastSeq });
  }

  close(): void {
    this.shouldRun = false;
    this.cleanup();
    this.setStatus('idle');
  }
}

export const eventSocket = new EventSocket();